/* eslint-disable max-lines-per-function */
import { api } from "jiffy-api";
import { notifyError } from "common/utils/app-monitoring/notify";
import { until } from "common/utils/helpers/delay";
import { getQueryParams } from "common/utils/browser";

const $getProductPageContainer = () => document.getElementById("product-page-container");
const $getMicrodata = () => document.getElementById("microdata");
const $getMetaProductName = () => $getMicrodata()?.querySelector('meta[itemprop="name"]');
const $getMetaBrand = () => $getMicrodata()?.querySelector('meta[itemprop="brand"]');
const $getMetaCatalogNumber = () => $getMicrodata()?.querySelector('meta[itemprop="sku"]');
const $getBusinessLine = () => document.getElementById("product-business-line");

const getFavoriteItems = async productSlug => {
  window.jiffy = window.jiffy || {};

  // return immediately when already fetched
  if ("favoriteItems" in window.jiffy) {
    return window.jiffy.favoriteItems;
  }

  // if being fetched, wait until fetched or reached wait timeout
  if (window.jiffy.fetchingFavoriteItems) {
    const favoriteItemsFetched = await until(() => "favoriteItems" in window.jiffy);
    if (favoriteItemsFetched) {
      return window.jiffy.favoriteItems;
    }
  }

  // when currently not fetching the favorite items or has reached wait
  // timeout (not successfully awaited), fallback to fetching favorite
  // items
  window.jiffy.fetchingFavoriteItems = true;
  try {
    const data = await api.favorites.getFavoriteItems({
      product_slug: productSlug
    });
    if (data.success) {
      window.jiffy.favoriteItems = data.items;
      window.jiffy.monitoredFavoriteItems = data.monitored_items;
      window.jiffy.fetchingFavoriteItems = false;
      return data.items;
    }
  } catch (error) {
    window.jiffy.fetchingFavoriteItems = false;
    // Skip reporting for rate-limited (429) responses — bot traffic triggers
    // Rack::Attack limits, producing non-actionable Honeybadger noise (HB-1696)
    if (error.status !== 429) {
      notifyError("Error fetching favorite items", {
        params: {
          error: JSON.stringify(error, Object.getOwnPropertyNames(error)),
          product_slug: productSlug
        }
      });
    }
  }

  return [];
};

const isFavorited = async (color, productSlug, businessLine = null) => {
  try {
    const favoriteItems = await getFavoriteItems(productSlug);

    // Handle new format with business line objects
    if (favoriteItems.length > 0 && typeof favoriteItems[0] === "object") {
      if (businessLine) {
        // Check for specific business line
        return favoriteItems.some(
          item => item.name === color && item.business_line === businessLine
        );
      }
      // Check if favorited in ANY business line
      return favoriteItems.some(item => item.name === color);
    }

    // Backwards compatibility with old format (array of color names)
    return favoriteItems.includes(color);
  } catch (error) {
    console.error("Error checking if item is favorited:", error);
    return false;
  }
};

/* eslint-disable max-params */
export const buildItemIdentifiers = (catalogNumber, color, isMadePdp, detailedProductName) => {
  const baseItemId = `${catalogNumber} ${color}`;
  const baseItemName = detailedProductName;

  return {
    itemId: isMadePdp ? `JiffyMade ${baseItemId}` : baseItemId,
    itemName: isMadePdp ? `Custom Printed ${baseItemName}` : baseItemName
  };
};

const isMadeMode = () => {
  const queryParams = getQueryParams();
  return queryParams.made === "true";
};

export const getItemPrice = (price, madeOnePlacementPrice) =>
  isMadeMode() ? (parseFloat(madeOnePlacementPrice) + parseFloat(price)).toFixed(2) : price;

export default async function getVariantInfo(variantId, isMadePdp = false) {
  const variantInput = document.querySelector(
    `.js-quantity-form-input[data-variant-id='${variantId}'], .js-grid-input[data-variant-id='${variantId}']`
  );
  const {
    size = "",
    isStandardSize = false,
    amount: sourcePrice = "",
    color = "",
    currency = "",
    fbData = "",
    fbEventId = "",
    compositionId = "",
    isLoggedIn = "",
    fulfillmentGroup = "",
    compositionInventoryRank = "",
    businessLine = "",
    image = "",
    colorVariantIds = "[]",
    madeOnePlacementPrice = "",
    rudderstackProductProperties = "{}",
    catalogNumber = ""
  } = variantInput?.dataset || {};

  let sku = variantInput?.dataset?.sku || "";
  const softnessScore = parseFloat(variantInput?.dataset?.compositionSoftnessScore) || null;

  let colorVariantIdsArr;
  try {
    colorVariantIdsArr = JSON.parse(colorVariantIds);
  } catch (error) {
    colorVariantIdsArr = [];
  }

  const {
    realProductId: productId,
    productId: productSlug,
    productCategory
  } = $getProductPageContainer()?.dataset || {};
  const isFavorite = await isFavorited(color, productSlug);
  const productName = $getMetaProductName()?.content;
  const brand = $getMetaBrand()?.content;
  const finalCatalogNumber = catalogNumber || $getMetaCatalogNumber()?.content;
  const price = getItemPrice(sourcePrice.replace("$", ""), madeOnePlacementPrice, isMadePdp);
  const { adwordsCategory } = $getMicrodata()?.dataset || {};

  const { itemId: itemGroupId, itemName: productAdwordsName } = buildItemIdentifiers(
    finalCatalogNumber,
    color,
    isMadePdp,
    productName
  );

  if (isMadePdp) {
    sku = [sku, variantInput?.dataset?.printVariantSku].join("-");
  }

  return {
    product: {
      id: productId,
      item_group_id: itemGroupId,
      name: productAdwordsName,
      catalog_number: finalCatalogNumber,
      category: productCategory,
      adwords_category: adwordsCategory,
      brand,
      business_line: businessLine || $getBusinessLine()?.value,
      image,
      ...JSON.parse(rudderstackProductProperties)
    },
    size,
    isStandardSize: isStandardSize === "true",
    color,
    price,
    sku,
    compositionInventoryRank,
    currency,
    fbData,
    fbEventId,
    compositionId,
    isLoggedIn,
    fulfillmentGroup,
    favorite: isFavorite,
    softnessScore,
    position: colorVariantIdsArr.indexOf(Number(variantId)) + 1
  };
}
